import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Flame, X, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format, subDays, isToday, parseISO } from 'date-fns';

export function StreakRiskBanner() {
  const { user } = useAuth();
  const [isAtRisk, setIsAtRisk] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    const checkStreakRisk = async () => {
      const { data: streak } = await supabase
        .from('user_streaks')
        .select('current_streak, last_activity_date')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!streak || streak.current_streak === 0) return;
      
      const lastActivity = streak.last_activity_date ? parseISO(streak.last_activity_date) : null;
      const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
      
      // Streak is at risk if last activity was yesterday (not today)
      if (lastActivity && format(lastActivity, 'yyyy-MM-dd') === yesterday) {
        setIsAtRisk(true);
        setCurrentStreak(streak.current_streak);
      }
    };
    
    checkStreakRisk();
  }, [user]);

  if (!isAtRisk || dismissed) return null;

  return (
    <Alert className="mb-6 border-orange-500/50 bg-orange-500/10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flame className="h-5 w-5 text-orange-500 animate-pulse" />
          <AlertDescription className="text-sm">
            <span className="font-medium">Seu streak de {currentStreak} dias está em risco!</span>
            {' '}Estude hoje para não perdê-lo.
          </AlertDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-500/10">
            <Link to="/questions" className="flex items-center gap-1">
              Estudar <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Alert>
  );
}
